import React, { useState, useEffect } from 'react';
import InfoPage from '@/components/InfoPage';
import { Smartphone, Menu, Share, Plus, Download } from 'lucide-react';

const AppPage = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check iOS
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIOS(ios);

    // Check Standalone
    const standalone = window.matchMedia("(display-mode: standalone)").matches;
    setIsStandalone(standalone);

    // Capture Android prompt
    const handleBeforeInstallPrompt = (e) => {
       e.preventDefault();
       setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
     if (!installPrompt) return;
     installPrompt.prompt();
     const { outcome } = await installPrompt.userChoice;
     if (outcome === 'accepted') {
        setInstallPrompt(null);
     }
  };

  return (
    <InfoPage title="Instale o App ParkingSpot">
      <div className="space-y-8">
        <section className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-xl border-2 border-purple-300">
          <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: '#302681' }}>
            📱 Use o App ParkingSpot seu Celular!
          </h2>
          <p className="text-center text-gray-700 mb-2">
            Você pode adicionar o ParkingSpot direto na sua tela inicial.
          </p>
          <p className="text-center text-sm text-gray-600">
            O App é rápido e grátis!
          </p>
        </section>

        {/* Visual Draw - Add to Home Screen */}
        <div className="flex justify-center py-4">
          <div className="relative bg-white p-3 rounded-[2rem] shadow-xl border-4 border-gray-800 w-48 h-80 flex flex-col items-center">
             {/* Speaker/Camera */}
             <div className="w-16 h-4 bg-gray-100 rounded-full mb-4"></div>
             
             {/* Screen content */}
             <div className="w-full h-full bg-slate-50 rounded-xl flex flex-col items-center justify-center space-y-4 relative overflow-hidden p-2">
                {/* App Icon (Favicon) */}
                <img src="/favicon.png" alt="ParkingSpot" className="w-16 h-16 rounded-xl shadow-md" />
                
                <p className="text-xs text-gray-600 font-semibold">ParkingSpot</p>
                
                {/* Install Button / iOS Hint */}
                {!isStandalone && (
                    <>
                        {installPrompt ? (
                            <button 
                                onClick={handleInstallClick}
                                className="bg-[#302681] text-white text-xs px-3 py-2 rounded-lg font-bold shadow-md hover:opacity-90 transition-opacity w-full"
                            >
                                Add na tela inicial
                            </button>
                        ) : isIOS ? (
                            <div className="text-[10px] text-gray-500 text-center px-1 bg-gray-100 rounded py-2">
                                Toque em <strong>Compartilhar</strong> <br/> e <strong>Adicionar à Tela de Início</strong>
                            </div>
                        ) : (
                            <div className="text-[10px] text-gray-400 text-center">
                                Acesse pelo Chrome para instalar
                            </div>
                        )}
                    </>
                )}
             </div>
             
             {/* Home Bar */}
             <div className="absolute bottom-2 w-16 h-1 bg-gray-300 rounded-full"></div>
          </div>
        </div>

        {/* Android Instructions */}
        <section className="bg-white p-6 rounded-xl shadow-lg border-2 border-green-400">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-green-500 text-white p-3 rounded-full">
              <Smartphone className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold" style={{ color: '#009440' }}>
              📱 Android (Chrome)
            </h3>
          </div>

          <ol className="space-y-6">
            <li className="flex space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">
                1
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Menu className="w-5 h-5 text-green-600" />
                  <p className="font-semibold text-lg">Toque nos <strong>3 pontinhos</strong> no canto superior direito</p>
                </div>
                <p className="text-gray-600 text-sm">Ícone do menu do Chrome (⋮)</p>
              </div>
            </li>

            <li className="flex space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">
                2
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Plus className="w-5 h-5 text-green-600" />
                  <p className="font-semibold text-lg">Selecione <strong>"Adicionar à tela inicial"</strong></p>
                </div>
                <p className="text-gray-600 text-sm">Ou "Instalar app" dependendo da versão do Android</p>
              </div>
            </li>

            <li className="flex space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">
                3
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Download className="w-5 h-5 text-green-600" />
                  <p className="font-semibold text-lg">Confirme tocando em <strong>"Adicionar"</strong></p>
                </div>
                <p className="text-gray-600 text-sm">Pronto! O ícone do ParkingSpot aparecerá na sua tela inicial</p>
              </div>
            </li>
          </ol>

          <div className="mt-6 bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
            <p className="text-sm text-green-800">
              <strong>✅ Pronto!</strong> Agora você pode abrir o ParkingSpot direto no ícone da tela inicial.
            </p>
          </div>
        </section>

        {/* iOS Instructions */}
        <section className="bg-white p-6 rounded-xl shadow-lg border-2 border-blue-400">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-blue-500 text-white p-3 rounded-full">
              <Smartphone className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold" style={{ color: '#302681' }}>
              🍎 iPhone/iPad (Safari)
            </h3>
          </div>

          <ol className="space-y-6">
            <li className="flex space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">
                1
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Share className="w-5 h-5 text-blue-600" />
                  <p className="font-semibold text-lg">Toque no botão <strong>Compartilhar</strong></p>
                </div>
                <p className="text-gray-600 text-sm">Ícone de compartilhar (quadrado com seta para cima) na barra inferior</p>
              </div>
            </li>

            <li className="flex space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">
                2
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Plus className="w-5 h-5 text-blue-600" />
                  <p className="font-semibold text-lg">Role para baixo e toque em <strong>"Adicionar à Tela de Início"</strong></p>
                </div>
                <p className="text-gray-600 text-sm">Procure pelo ícone de + ao lado do texto</p>
              </div>
            </li>

            <li className="flex space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">
                3
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Download className="w-5 h-5 text-blue-600" />
                  <p className="font-semibold text-lg">Toque em <strong>"Adicionar"</strong> no canto superior direito</p>
                </div>
                <p className="text-gray-600 text-sm">Pronto! O ícone do ParkingSpot aparecerá na sua tela inicial</p>
              </div>
            </li>
          </ol>

          <div className="mt-6 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
            <p className="text-sm text-blue-800">
              <strong>✅ Pronto!</strong> Agora você pode abrir o ParkingSpot direto no ícone da tela inicial.
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl border-2 border-yellow-400">
          <h3 className="text-xl font-bold mb-4 text-center" style={{ color: '#831D1C' }}>
            🎉 Vantagens de usar como App
          </h3>
          <ul className="grid md:grid-cols-2 gap-4">
            <li className="flex items-start space-x-3">
              <span className="text-2xl">⚡</span>
              <div className="font-semibold">Acesso Rápido</div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="text-2xl">📱</span>
              <div className="font-semibold">Experiência de App</div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="text-2xl">💾</span>
              <div className="font-semibold">Sem Instalação</div>
            </li>
            <li className="flex items-start space-x-3">
              <span className="text-2xl">🔔</span>
              <div className="font-semibold">Receba avisos notificações</div>
            </li>
          </ul>
        </section>

        {/* Troubleshooting */}
        <section className="bg-gray-50 p-6 rounded-xl">
          <h3 className="text-lg font-semibold mb-3" style={{ color: '#302681' }}>
            ❓ Não encontrou a opção?
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• <strong>Android:</strong> Certifique-se de estar usando o Chrome (não outros navegadores)</li>
            <li>• <strong>iPhone:</strong> Use o Safari (não funciona no Chrome do iPhone)</li>
            <li>• <strong>Precisa de ajuda?</strong> Entre em contato: contato@parkingspot.com.br</li>
          </ul>
        </section>
      </div>
    </InfoPage>
  );
};

export default AppPage;
